import type {
  CitizenReportAttachmentKind,
  CitizenReportSubmission,
  CitizenReportSubmissionResult,
  CitizenReportSubmitStage,
} from '@/types/citizenReport'

export const MAX_REPORT_PHOTO_BYTES = 10 * 1024 * 1024
export const MAX_REPORT_VIDEO_BYTES = 100 * 1024 * 1024
export const REPORT_PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif'
export const REPORT_VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/webm'

const allowedPhotoTypes = new Set(REPORT_PHOTO_ACCEPT.split(','))
const allowedVideoTypes = new Set(REPORT_VIDEO_ACCEPT.split(','))

const apiUrl = import.meta.env.VITE_CITIZEN_REPORTS_API_URL

interface ApiResponse {
  ok: boolean
  error?: string
  message?: string
}

interface DraftResponse extends ApiResponse {
  reportId: string
  uploadToken: string
}

interface UploadSessionResponse extends ApiResponse {
  uploadUrl: string
}

interface DriveUploadResponse {
  id?: string
  name?: string
}

interface FinalizeResponse extends ApiResponse {
  reportId: string
  status: 'pending'
}

const apiErrorMessages: Record<string, string> = {
  service_disabled: 'El canal de reportería está desactivado temporalmente.',
  invalid_topic: 'El tema seleccionado ya no está disponible.',
  location_required: 'No se pudo asociar el reporte a una ubicación.',
  description_too_short: 'La descripción debe tener al menos 20 caracteres.',
  invalid_attachment_type: 'El formato del archivo no está permitido.',
  attachment_too_large: 'El archivo supera el tamaño permitido.',
  report_draft_expired: 'La sesión de envío expiró. Intenta enviar el reporte nuevamente.',
  drive_session_error: 'No se pudo preparar la carga del archivo en Drive.',
  invalid_origin: 'El origen de la página no es válido para cargar archivos.',
  origin_not_allowed: 'Este dominio no está autorizado para cargar archivos.',
  invalid_contact_email: 'El correo de contacto no tiene un formato válido.',
  invalid_contact_phone: 'El número de contacto no tiene un formato válido.',
}

function getApiUrl() {
  if (!apiUrl) throw new Error('Falta VITE_CITIZEN_REPORTS_API_URL')
  return apiUrl
}

async function postAction<T extends ApiResponse>(payload: Record<string, unknown>): Promise<T> {
  const response = await fetch(getApiUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'text/plain;charset=utf-8',
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) throw new Error('No se pudo contactar el canal de reportería')

  const result = (await response.json()) as T
  if (!result.ok) {
    const errorCode = result.error || ''
    throw new Error(apiErrorMessages[errorCode] || result.message || 'No se pudo enviar el reporte')
  }
  return result
}

async function uploadAttachment(
  reportId: string,
  uploadToken: string,
  kind: CitizenReportAttachmentKind,
  file: File,
) {
  const session = await postAction<UploadSessionResponse>({
    action: 'create_upload_session',
    reportId,
    uploadToken,
    kind,
    fileName: file.name,
    mimeType: file.type,
    size: file.size,
    origin: window.location.origin,
  })

  let uploadResponse: Response
  try {
    uploadResponse = await fetch(session.uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    })
  } catch {
    throw new Error(
      'Drive bloqueó la carga desde este dominio. Revisa REPORTS_ALLOWED_ORIGINS y vuelve a desplegar Apps Script.',
    )
  }

  if (!uploadResponse.ok) throw new Error(`No se pudo subir ${kind === 'photo' ? 'la foto' : 'el video'}`)

  const uploadedFile = (await uploadResponse.json()) as DriveUploadResponse
  if (!uploadedFile.id) throw new Error('Drive no devolvió el identificador del archivo')
  return uploadedFile.id
}

export function isCitizenReportsConfigured() {
  return Boolean(apiUrl)
}

export function validateCitizenReportFile(kind: CitizenReportAttachmentKind, file: File) {
  const isExpectedType =
    kind === 'photo' ? allowedPhotoTypes.has(file.type) : allowedVideoTypes.has(file.type)
  if (!isExpectedType) {
    return kind === 'photo' ? 'Selecciona un archivo de imagen.' : 'Selecciona un archivo de video.'
  }

  const maxBytes = kind === 'photo' ? MAX_REPORT_PHOTO_BYTES : MAX_REPORT_VIDEO_BYTES
  if (file.size > maxBytes) {
    const maxMegabytes = Math.round(maxBytes / 1024 / 1024)
    return `El archivo supera el máximo de ${maxMegabytes} MB.`
  }

  return ''
}

export async function submitCitizenReport(
  submission: CitizenReportSubmission,
  onStage?: (stage: CitizenReportSubmitStage) => void,
): Promise<CitizenReportSubmissionResult> {
  onStage?.('creating')
  const draft = await postAction<DraftResponse>({
    action: 'create_report',
    clientRequestId: crypto.randomUUID(),
    topicId: submission.topicId,
    description: submission.description,
    provinceId: submission.territory.provinceId,
    provinceName: submission.territory.provinceName,
    cantonId: submission.territory.cantonId,
    cantonName: submission.territory.cantonName,
    contactName: submission.contactName || '',
    contactEmail: submission.contactEmail || '',
    contactPhone: submission.contactPhone || '',
  })

  let photoFileId = ''
  let videoFileId = ''

  if (submission.photo) {
    onStage?.('uploading-photo')
    photoFileId = await uploadAttachment(
      draft.reportId,
      draft.uploadToken,
      'photo',
      submission.photo,
    )
  }

  if (submission.video) {
    onStage?.('uploading-video')
    videoFileId = await uploadAttachment(
      draft.reportId,
      draft.uploadToken,
      'video',
      submission.video,
    )
  }

  onStage?.('finalizing')
  const result = await postAction<FinalizeResponse>({
    action: 'finalize_report',
    reportId: draft.reportId,
    uploadToken: draft.uploadToken,
    photoFileId,
    videoFileId,
  })

  return {
    reportId: result.reportId,
    status: result.status,
  }
}
